import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html, nothing } from 'lit';
import {
  getEditableImportColumns,
  IMPORT_BOOKING_FIELDS,
  IMPORT_FIELD_LABELS,
  importValueToCsv,
  PersonImportData,
} from '../entities/PersonImportConvertConfig.js';
import { CHILD_ID_FIELD_SPECIFIER_ID, type PersonImportStrategy } from '../entities/PersonImportStrategy.js';
import {
  DuplicateMatches,
  editImportRow,
  findingLabel,
  getConfidenceUi,
  getSeverityUi,
  ImportPersonPicker,
  MATCHED_BY_LABEL,
} from './PersonImportStrategyDataTable.js';

/**
 * Everything about one row in one place - what the import noticed, whom it is for, and every value the
 * file carries next to the stored one - and the place to correct those values instead of in the csv.
 * A guardian's row has no class booking, so those columns are left out there.
 */
const PersonImportRowEditDialog = stateful(
  ({
    strategy,
    resolve,
    reject,
  }: {
    strategy: PersonImportStrategy;
    resolve: (saved: boolean) => void;
    reject: () => void;
  }) => {
    const [, rerender] = useState({});
    const [saving, setSaving] = useState(false);

    const data = strategy.rawImportData ?? new PersonImportData({});
    const columns = getEditableImportColumns().filter(
      ({ field }) => !(data.isSbS && IMPORT_BOOKING_FIELDS.includes(field)),
    );
    const conflicts = strategy.fileConflicts ?? [];
    const specifiers = strategy.fieldSpecifiers ?? [];
    const childLink = specifiers.find(specifier => specifier.dbFieldPath === CHILD_ID_FIELD_SPECIFIER_ID);
    const findings = [...(strategy.findings ?? [])].sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0));
    const { confidence, badgeClass } = getConfidenceUi(strategy.matchConfidence);

    const initial = useMemo(
      () => [
        Object.fromEntries(columns.map(({ column, field }) => [column, importValueToCsv(data, field)])) as Record<
          string,
          string
        >,
      ],
      [strategy._id],
    );
    // mutated in place - the inputs hold what is typed, a rerender is only needed when a conflict value is picked
    const values = useMemo(() => [{ ...initial }], [initial]);

    const isDeviation = (field: keyof PersonImportData) =>
      conflicts.some(conflict => conflict.field === field) ||
      specifiers.some(specifier => specifier.csvFieldName === field && specifier.from !== specifier.to);
    const [onlyDeviations, setOnlyDeviations] = useState(columns.some(({ field }) => isDeviation(field)));
    const shownColumns = onlyDeviations ? columns.filter(({ field }) => isDeviation(field)) : columns;

    const save = async () => {
      /**
       * Only what the user changed - and every column the file disagrees on, even when the value shown
       * is kept: saving it is the user's answer to the conflict.
       */
      const cells = columns
        .filter(
          ({ column, field }) =>
            values[column] !== initial[column] || conflicts.some(conflict => conflict.field === field),
        )
        .map(({ column }) => ({ column, value: values[column] ?? '' }));
      if (!cells.length) return resolve(false);

      setSaving(true);
      try {
        await editImportRow(strategy, cells);
        resolve(true);
      } catch {
        setSaving(false);
      }
    };

    const name = `${data.stammdaten_vorname ?? ''} ${data.stammdaten_nachname ?? ''}`.trim();

    return html`
      <div class="flex flex-col">
        <div class="border-b border-base-300 px-6 pt-6 pb-4">
          ${DialogHeader({
            title: `${name || 'Zeile'}${data.isSbS ? ' (Sorgeberechtigte Person)' : ''}`,
            subtitle: strategy.childNames?.length
              ? `Steht in der Datei bei ${strategy.childNames.join(', ')}`
              : 'Werte werden wie in der CSV-Datei geschrieben und genauso geprüft.',
            close: reject,
            closeLabel: 'Dialog schließen',
          })}
        </div>

        <div class="flex flex-col gap-5 px-6 py-5">
          ${
            findings.length
              ? html`
                  <div class="flex flex-col gap-2">
                    ${findings.map(finding => {
                      const ui = getSeverityUi(finding.severity);
                      return html`
                        <div role="alert" class="alert alert-soft ${ui.alertClass} items-start text-sm">
                          ${ui.icon}
                          <div><b>${findingLabel(finding)}.</b> ${finding.message}</div>
                        </div>
                      `;
                    })}
                  </div>
                `
              : nothing
          }

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Zugeordnete Person</legend>
            <div class="flex flex-wrap items-center gap-3">
              <div class="w-96 max-w-full">${ImportPersonPicker(strategy, () => rerender({}))}</div>
              <span class="badge ${badgeClass}">${Math.round(confidence * 100)} %</span>
              <span class="text-sm text-base-content/70">
                ${strategy.matchedBy ? MATCHED_BY_LABEL[strategy.matchedBy] : 'niemand passt'}
              </span>
              ${
                strategy.resolvedId
                  ? html`
                      <a
                        class="link link-primary ml-auto text-sm"
                        href="/personen/${strategy.resolvedId}"
                        target="_blank"
                        rel="noopener"
                      >
                        Person öffnen <span class="icon-[material-symbols--open-in-new] align-middle"></span>
                      </a>
                    `
                  : nothing
              }
            </div>
            <p class="label">Leer lassen legt eine neue Person an. Eine Auswahl wird sofort übernommen.</p>
            ${strategy.duplicateMatchIDs?.length ? DuplicateMatches({ ids: strategy.duplicateMatchIDs }) : nothing}
          </fieldset>

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-4">
              <h3 class="font-semibold">Werte</h3>
              <label class="label gap-2 text-sm">
                <input
                  type="checkbox"
                  class="toggle toggle-xs"
                  .checked=${onlyDeviations}
                  @change=${(event: Event) => setOnlyDeviations((event.target as HTMLInputElement).checked)}
                />
                Nur Abweichungen zeigen
              </label>
            </div>
            <div class="overflow-x-auto rounded-box border border-base-300">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="w-48">Feld</th>
                    <th>Gespeichert</th>
                    <th>Aus der Datei – wird geschrieben</th>
                  </tr>
                </thead>
                <tbody>
                  ${shownColumns.map(({ column, field }) => {
                    const conflict = conflicts.find(candidate => candidate.field === field);
                    const stored = specifiers.find(specifier => specifier.csvFieldName === field)?.from ?? '';
                    const label = IMPORT_FIELD_LABELS[field] ?? column;

                    return html`
                      <tr class="${conflict ? 'bg-error/10' : isDeviation(field) ? '' : 'text-base-content/70'}">
                        <td class="font-medium">${label}</td>
                        <td class="text-base-content/70">${stored || '—'}</td>
                        <td>
                          <div class="flex flex-col gap-1.5">
                            ${TextInput({
                              value: values[column],
                              ariaLabel: label,
                              placeholder: 'leer – bleibt wie gespeichert',
                              placeholderMode: 'hide',
                              error: conflict ? 'Die Datei nennt hierfür unterschiedliche Werte' : undefined,
                              onPicked: value => {
                                values[column] = value ?? '';
                              },
                            })}
                            ${
                              conflict
                                ? html`
                                    <div class="flex flex-wrap items-center gap-2 text-sm text-base-content/70">
                                      In der Datei:
                                      <div class="join">
                                        ${(conflict.values ?? []).map(
                                          value => html`
                                            <button
                                              type="button"
                                              class="btn btn-sm join-item ${values[column] === value ? 'btn-primary' : ''}"
                                              @click=${() => {
                                                values[column] = value;
                                                rerender({});
                                              }}
                                            >
                                              ${value}
                                            </button>
                                          `,
                                        )}
                                      </div>
                                    </div>
                                  `
                                : nothing
                            }
                          </div>
                        </td>
                      </tr>
                    `;
                  })}
                  ${
                    childLink
                      ? html`
                          <tr>
                            <td class="font-medium">Sorgeberechtigung für</td>
                            <td class="text-base-content/70">
                              ${(childLink.from ?? '').split(', ').filter(Boolean).length} Kind(er)
                            </td>
                            <td>
                              ${(strategy.childNames ?? []).map(
                                child =>
                                  html`<span class="badge badge-primary badge-soft badge-sm mr-1">+ ${child}</span>`,
                              )}
                              <span class="text-sm text-base-content/70">nicht bearbeitbar</span>
                            </td>
                          </tr>
                        `
                      : nothing
                  }
                </tbody>
              </table>
            </div>
            <p class="text-sm text-base-content/70">
              Die Datei selbst bleibt unverändert – beim nächsten Import gelten wieder ihre Werte.
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-2 border-t border-base-300 px-6 py-3">
          <button class="btn btn-ghost" @click=${() => reject()}>Abbrechen</button>
          <button class="btn btn-primary" ?disabled=${saving} @click=${save}>
            ${saving ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Speichern und neu
            berechnen
          </button>
        </div>
      </div>
    `;
  },
);

/** @returns whether the row was changed */
export async function openPersonImportRowEditDialog(strategy: PersonImportStrategy): Promise<boolean> {
  return showDialog<boolean>({
    content: (resolve, reject) => PersonImportRowEditDialog({ strategy, resolve, reject }),
    // p-0 + flex column: the modal-box is the only scroll container
    modalBoxClasses: 'flex flex-col max-w-4xl p-0',
  }).catch(() => false);
}
